import { useTranslator } from '@Localization';
import { IconButton, Input } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { ISelectOption } from '../../../../../../../../design/inputs/select/ISelectOption';
import { Select } from '../../../../../../../../design/inputs/select/Select';
import { S_TextInput } from '../../../../../../../../design/inputs/textInput/S_TextInput';
import { S_FieldLabel } from '../../../../../../../../design/labels/fieldLabel/S_FIeldLabel';
import { TrashIcon } from '../../../../../../../../design/static/icons/TrashIcon';
import { QuestionType } from '../../../../../../annotation/question.model';
import { AnnotationToolFilterType, IAnnotationToolFilter, IImageFiltersData } from '../../../imageFilters.model';

interface IProps {
  availableImageFilters: IImageFiltersData;
  toolFilter: IAnnotationToolFilter;
  showDelete: boolean;
  onDelete: () => void;
  onL1FilterChanged: (value: string | null) => void;
  onL2FilterChanged: (value: string | null) => void;
  onL3MultiselectFilterChanged: (value: string[]) => void;
  onL3SelectFilterChanged: (value: string | null) => void;
  onL3TextFilterChanged: (value: string) => void;
  onL3MinNumberFilterChanged: (value: string) => void;
  onL3MaxNumberFilterChanged: (value: string) => void;
}

export const AnnotationToolRowView = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();

  const handleL1FilterChanged = (value: string | null): void => {
    props.onL1FilterChanged(value);
  };

  const handleL2FilterChange = (value: string | null): void => {
    props.onL2FilterChanged(value);
  };

  const handleL3SelectChange = (value: string | null): void => {
    props.onL3SelectFilterChanged(value);
  };

  const handleL3MultiselectFilterChanged = (value: string[]): void => {
    props.onL3MultiselectFilterChanged(value);
  };

  const selectFilterTypes = [AnnotationToolFilterType.AttributeAnswer, AnnotationToolFilterType.QuestionAnswer];

  let attributesOptions: { value: string; label: string }[] = [];
  let answerOptions: ISelectOption<string>[] = [];
  let questionType: QuestionType | undefined = undefined;

  const markingToolOptions = [
    ...props.availableImageFilters.annotationTypes
      .slice()
      .sort((x, y) => x.order - y.order)
      .map((x) => {
        return { value: x.id, label: x.name };
      }),
    ...props.availableImageFilters.questionFilters
      .slice()
      .sort((x, y) => x.order - y.order)
      .map((x) => {
        return { value: x.id, label: x.text };
      }),
  ];

  if (props.toolFilter.l1FilterType === AnnotationToolFilterType.AnnotationTool) {
    const annotationType = props.availableImageFilters.annotationTypes.find((x) => x.id === props.toolFilter.l1Filter);
    if (annotationType) {
      attributesOptions =
        annotationType.attributes
          .slice()
          .sort((x, y) => x.order - y.order)
          .map((x) => {
            return { value: x.id, label: x.text };
          }) || [];

      const attribute = annotationType.attributes.find((x) => x.id === props.toolFilter.l2Filter);
      if (attribute) {
        questionType = attribute.answerType;
        if (selectFilterTypes.some((x) => x === props.toolFilter.l3FilterType)) {
          answerOptions = attribute.possibleAnswers
            .slice()
            .sort((x, y) => x.order - y.order)
            .map((x) => {
              return { value: x.id, label: x.text };
            });
        }
      }
    }
  } else if (props.toolFilter.l1FilterType === AnnotationToolFilterType.Question) {
    const question = props.availableImageFilters.questionFilters.find((x) => x.id === props.toolFilter.l1Filter);
    if (question) {
      questionType = question.answerType;
      if (selectFilterTypes.some((x) => x === props.toolFilter.l3FilterType)) {
        answerOptions = question.possibleAnswers
          .slice()
          .sort((x, y) => x.order - y.order)
          .map((x) => ({ value: x.id, label: x.text }));
      }
    }
  }

  return (
    <div className='annotation-tool-filter'>
      <div className='annotation-tool-filter-option'>
        <S_FieldLabel>{t('annotation_tool')}</S_FieldLabel>
        <Select
          disableClearable={false}
          placeholder={t('select_dots')}
          id='select-l1'
          value={markingToolOptions.find((x) => x.value === props.toolFilter.l1Filter)}
          onChange={(_, v) => {
            handleL1FilterChanged(v?.value ?? '');
          }}
          options={markingToolOptions}
        />
      </div>

      {props.toolFilter.l1FilterType === AnnotationToolFilterType.AnnotationTool && attributesOptions.length > 0 && (
        <div className='annotation-tool-filter-option'>
          <S_FieldLabel>{t('attribute')}</S_FieldLabel>
          <Select
            disableClearable={false}
            placeholder={t('select_dots')}
            id='select-l2'
            value={attributesOptions.find((x) => x.value === props.toolFilter.l2Filter)}
            onChange={(_, v) => {
              handleL2FilterChange(v?.value ?? '');
            }}
            options={attributesOptions}
          />
        </div>
      )}

      {questionType === QuestionType.MULTISELECT && answerOptions.length > 0 && (
        <div className='annotation-tool-filter-option'>
          <S_FieldLabel>{t('values')}</S_FieldLabel>
          <Select
            disableClearable={false}
            multiple
            placeholder={t('select_dots')}
            id='select3-multi'
            onChange={(_, v) => {
              handleL3MultiselectFilterChanged(v.map((x) => x.value));
            }}
            value={answerOptions.filter((x) => props.toolFilter.l3Filters.some((l) => l === x.value))}
            options={answerOptions}
          />
        </div>
      )}

      {[QuestionType.ALTERNATIVE, QuestionType.SET_TRAIN_TEST, QuestionType.STATUS_GOOD_BAD, QuestionType.TRUE_FALSE].some((x) => x === questionType) &&
        answerOptions.length > 0 && (
          <div className='annotation-tool-filter-option'>
            <S_FieldLabel>{t('value')}</S_FieldLabel>
            <Select
              disableClearable={false}
              id='select3-single'
              placeholder={t('select_dots')}
              onChange={(_, v) => {
                handleL3SelectChange(v?.value ?? '');
              }}
              value={answerOptions.find((x) => x.value === props.toolFilter.l3Filters[0])}
              options={answerOptions}
            />
          </div>
        )}

      {questionType === QuestionType.OPEN && (
        <div className='annotation-tool-filter-option'>
          <S_FieldLabel>{t('value')}</S_FieldLabel>
          <S_TextInput
            value={props.toolFilter.l3Filters.slice()[0] || ''}
            type='text'
            placeholder={t('answer')}
            onChange={props.onL3TextFilterChanged}
          />
        </div>
      )}

      {questionType === QuestionType.NUMBER && (
        <>
          <div className='annotation-tool-filter-option number'>
            <S_FieldLabel>{t('min')}</S_FieldLabel>
            <Input
              type='number'
              value={props.toolFilter.l3Filters.slice()[0]}
              onChange={(e) => {
                props.onL3MinNumberFilterChanged(e.target.value);
              }}
            />
          </div>
          <div className='annotation-tool-filter-option number'>
            <S_FieldLabel>{t('max')}</S_FieldLabel>
            <Input
              type='number'
              value={props.toolFilter.l3Filters.slice()[1]}
              onChange={(e) => {
                props.onL3MaxNumberFilterChanged(e.target.value);
              }}
            />
          </div>
        </>
      )}
      {props.showDelete && (
        <IconButton
          onClick={props.onDelete}
          title={t('remove')}
        >
          <TrashIcon />
        </IconButton>
      )}
    </div>
  );
});
